/**
 * L.6 — Specialize-on-bundle button + status row.
 *
 * Sits at the bottom of the SkillLibraryWindow when ≥1 row is checked.
 * Fires `tinkerTrainingJobs.request` with a `skill-bundle:<json>`
 * dataset scope so the trainer pulls each named skill's current
 * version body into the dataset. Status banner mirrors the per-row
 * TrainLoraButton lifecycle.
 */
import { useState } from 'react';
import { useAction, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import { useWindowContext } from '../desktop/WindowContext';
import { setLabHandoff } from '../specialization-lab/labHandoff';

interface BundleSpecializeButtonProps {
  selectedNames: string[];
  onClear: () => void;
}

type LocalStatus = 'idle' | 'requesting' | 'kicking' | 'queued' | 'error';

export function BundleSpecializeButton({
  selectedNames,
  onClear,
}: BundleSpecializeButtonProps): React.ReactElement | null {
  const requestJob = useMutation(api.tinkerTrainingJobs.request);
  const kickWorker = useAction(api.tinkerTrainingJobs.kickWorker);
  const { openWindow } = useWindowContext();
  const [status, setStatus] = useState<LocalStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  if (selectedNames.length === 0) return null;

  const isBusy = status === 'requesting' || status === 'kicking';

  async function handleClick(): Promise<void> {
    setError(null);
    setStatus('requesting');
    try {
      const jobId = await requestJob({
        baseModel: 'Qwen/Qwen3-8B',
        loraRank: 8,
        epochs: 1,
        learningRate: 0.0002,
        costCapUsd: 5,
        datasetScopeJson: JSON.stringify([
          `skill-bundle:${JSON.stringify(selectedNames)}`,
        ]),
      });
      setStatus('kicking');
      const result = await kickWorker({
        jobId,
      });
      if (!result.ok) {
        setStatus('error');
        setError(result.reason ?? 'Worker unreachable.');
        return;
      }
      setStatus('queued');
      onClear();
      // L.10 — open the Lab eagerly. The Lab itself reads the latest
      // complete training run; while the bundle job is still queued the
      // user sees the in-progress state from the prior compare. Once
      // the new run completes the Lab refreshes on the next render.
      // F18.G.3 — seed the handoff with the first skill in the bundle
      // so the Lab's header picker lands on a skill the user just
      // touched, not on whatever ships first in the marketplace.
      const firstSkill = selectedNames[0];
      if (firstSkill !== undefined) {
        setLabHandoff({
          skillName: firstSkill,
          source: 'BundleSpecializeButton',
        });
      }
      openWindow('/specialization-lab');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const label =
    selectedNames.length === 1
      ? 'Specialize the model on this skill'
      : `Specialize the model on ${selectedNames.length} skills`;

  return (
    <div
      className="border-t-2 border-solid border-t-[#808080] p-2 bg-[#ece9d8] flex flex-col gap-1"
      style={{ borderTopColor: '#808080' }}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={testIds.skills.bundleSpecializeButton}
          onClick={() => void handleClick()}
          disabled={isBusy}
          className="text-[11px] px-3 py-1 cursor-pointer font-bold disabled:opacity-50"
          style={{
            background: 'linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4)',
            color: '#000',
            border: '1px solid #003c74',
            borderRadius: 3,
          }}
        >
          {status === 'requesting'
            ? 'Snapshotting…'
            : status === 'kicking'
              ? 'Kicking worker…'
              : status === 'queued'
                ? 'Queued — Lab opens when complete'
                : label}
        </button>
        <button
          type="button"
          data-testid={testIds.skills.bundleClearButton}
          onClick={onClear}
          disabled={isBusy}
          className="text-[11px] px-2 py-1 cursor-pointer disabled:opacity-50"
          style={{
            background: '#ece9d8',
            color: '#000',
            border: '1px solid #808080',
            borderRadius: 3,
          }}
        >
          Clear ({selectedNames.length})
        </button>
      </div>
      {status === 'error' && error !== null && (
        <span
          data-testid={testIds.skills.bundleSpecializeStatus}
          className="text-[10px] text-[#7a0a0a] font-bold"
        >
          {error}
        </span>
      )}
      {status === 'queued' && (
        <span
          data-testid={testIds.skills.bundleSpecializeStatus}
          className="text-[10px] text-black"
        >
          Specialization in progress — the Lab opens automatically when it
          finishes.
        </span>
      )}
    </div>
  );
}
